// client/src/scenes/transactions/TransactionBreakdownForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  Typography,
  IconButton,
  Paper,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { gql, useQuery } from "@apollo/client";
import { groupClient } from "state/apolloClient";

// ---- GraphQL schedules (same as TransactionForm) ----
const GET_UNPAID_LOAN_SCHEDULES = gql`
  query {
    loanRepaymentSchedulesNotPaid {
      id
      loanApplicationId
      dueDate
      status
      principalDue
      principalPaid
      interestDue
      interestPaid
    }
  }
`;

const GET_CONTRIBUTION_SCHEDULE = gql`
  query ContributionScheduleByUser {
    contributionScheduleByUser(userId: null, paid: false) {
      id
      month
      scheduleYear
      dueDate
      expectedAmount
    }
  }
`;

const GET_PENDING_MEMBER_FINES = gql`
  query PendingMemberFineView {
    pendingMemberFineView {
      id
      fine
      dueDate
      amountDue
      amountPaid
      fineTypeId
    }
  }
`;

const SCHEDULE_TYPES = [
  { id: 1, label: "Loan" },
  { id: 2, label: "Contribution" },
  { id: 3, label: "Fine" },
];

const currency = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? n.toLocaleString()
    : n
    ? Number(n).toLocaleString()
    : "-";

const normalizeInitialBreakdowns = (initialBreakdowns) => {
  if (!Array.isArray(initialBreakdowns) || initialBreakdowns.length === 0) {
    return [
      {
        scheduleTypeId: 1,
        scheduleType: "Loan",
        scheduleId: "",
        allocatedAmount: "",
        status: "Pending",
      },
    ];
  }

  // Map server rows into the editable shape the form expects
  return initialBreakdowns.map((b) => ({
    scheduleTypeId: Number(b.scheduleTypeId ?? 1),
    scheduleType:
      b.scheduleType ||
      SCHEDULE_TYPES.find((t) => t.id === Number(b.scheduleTypeId))?.label ||
      "Loan",
    scheduleId: b.scheduleId ?? "",
    allocatedAmount:
      b.allocatedAmount !== null && b.allocatedAmount !== undefined
        ? String(b.allocatedAmount)
        : "",
    status: b.status ?? "Pending",
    processingErrorMessage: b.processingErrorMessage ?? null,
  }));
};

const TransactionBreakdownForm = ({
  open,
  onClose,
  transaction,
  onSubmit,
  loading,
  mode = "edit", // ✅ "view" | "edit"
  initialBreakdowns = [], // ✅ prefilled from allocationQueueByTransaction
}) => {
  const isViewMode = mode === "view";
  const [breakdowns, setBreakdowns] = useState([
    { scheduleTypeId: 1, scheduleType: "Loan", scheduleId: "", allocatedAmount: "", status: "Pending" },
  ]);

  // Load schedules (still useful in view mode for nice dropdown labels)
  const { data: loanData, loading: loadingLoans } = useQuery(GET_UNPAID_LOAN_SCHEDULES, {
    skip: !open,
  });
  const { data: contribData, loading: loadingContrib } = useQuery(GET_CONTRIBUTION_SCHEDULE, {
    client: groupClient,
    skip: !open,
  });
  const { data: finesData, loading: loadingFines } = useQuery(GET_PENDING_MEMBER_FINES, {
    client: groupClient,
    skip: !open,
  });

  useEffect(() => {
    if (!open) {
      setBreakdowns([{ scheduleTypeId: 1, scheduleType: "Loan", scheduleId: "", allocatedAmount: "", status: "Pending" }]);
      return;
    }

    // ✅ Prefill from server-provided breakdown rows
    setBreakdowns(normalizeInitialBreakdowns(initialBreakdowns));
  }, [open, transaction?.id, initialBreakdowns]);

  const loanOptions = useMemo(() => {
    const loanRows = loanData?.loanRepaymentSchedulesNotPaid || [];
    return loanRows.map((r) => {
        const outstanding = (r.principalDue - r.principalPaid) + (r.interestDue - r.interestPaid);
        return {
          id: r.id,
          label: `#${r.id} • App ${r.loanApplicationId} • Due ${new Date(r.dueDate).toLocaleDateString()} • ${r.status} • Outstanding KES ${currency(
            outstanding
          )}`,
        };
      });
  }, [loanData?.loanRepaymentSchedulesNotPaid]);

  const contribOptions = useMemo(() => {
    const contribRows = contribData?.contributionScheduleByUser || [];
    return contribRows.map((r) => ({
        id: r.id,
        label: `#${r.id} • ${r.month}/${r.scheduleYear} • Due ${new Date(r.dueDate).toLocaleDateString()} • Expected KES ${currency(
          r.expectedAmount
        )}`,
      }));
  }, [contribData?.contributionScheduleByUser]);

  const fineOptions = useMemo(() => {
    const fineRows = finesData?.pendingMemberFineView || [];
    return fineRows.map((r) => {
      const outstanding = Number(r.amountDue || 0) - Number(r.amountPaid || 0);
      return {
        id: r.id,
        label: `#${r.id} • ${r.fine || `Type ${r.fineTypeId}`} • Due ${new Date(
          r.dueDate
        ).toLocaleDateString()} • Outstanding KES ${currency(outstanding)}`,
      };
    });
  }, [finesData?.pendingMemberFineView]);

  const addRow = () => {
    if (isViewMode) return;
    setBreakdowns((prev) => [
      ...prev,
      { scheduleTypeId: 1, scheduleType: "Loan", scheduleId: "", allocatedAmount: "", status: "Pending" },
    ]);
  };

  const removeRow = (idx) => {
    if (isViewMode) return;
    setBreakdowns((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, key, value) => {
    if (isViewMode) return;
    setBreakdowns((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  const handleTypeChange = (idx, typeId) => {
    if (isViewMode) return;

    const st = SCHEDULE_TYPES.find((t) => t.id === Number(typeId));
    setBreakdowns((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, scheduleTypeId: st.id, scheduleType: st.label, scheduleId: "" } : row
      )
    );
  };

  const total = useMemo(
    () => breakdowns.reduce((acc, b) => acc + (Number(b.allocatedAmount) || 0), 0),
    [breakdowns]
  );

  const canSubmit = useMemo(() => {
    if (isViewMode) return false;
    const validRows = breakdowns.every(
      (b) =>
        b.scheduleTypeId &&
        b.scheduleId !== "" &&
        b.allocatedAmount !== "" &&
        Number(b.allocatedAmount) > 0
    );
    return breakdowns.length > 0 && validRows;
  }, [breakdowns, isViewMode]);

  const submit = () => {
    if (isViewMode) return;
    if (!canSubmit) return;

    const payloadRows = breakdowns.map((b) => ({
      scheduleType: SCHEDULE_TYPES.find((t) => t.id === Number(b.scheduleTypeId))?.label || b.scheduleType || "",
      scheduleTypeId: Number(b.scheduleTypeId),
      scheduleId: Number(b.scheduleId),
      allocatedAmount: Number.parseFloat(b.allocatedAmount),
    }));

    onSubmit(payloadRows);
  };

  const title = isViewMode ? "View Breakdown" : "Edit Breakdown";

  const amount = Number(transaction?.amount ?? 0);
  const mismatch = useMemo(() => {
    const eps = 0.01;
    return Math.abs(total - amount) > eps;
  }, [total, amount]);

  const StatusChip = ({ value }) => {
    if (!value) return null;
    // keep simple: default colors
    return <Chip size="small" label={value} sx={{ ml: 1 }} />;
  };

  const getScheduleLabel = (row) => {
    const scheduleId = Number(row.scheduleId);
    if (!scheduleId) return "-";

    if (Number(row.scheduleTypeId) === 1) {
      return loanOptions.find((opt) => Number(opt.id) === scheduleId)?.label || `Loan Schedule #${scheduleId}`;
    }

    if (Number(row.scheduleTypeId) === 2) {
      return (
        contribOptions.find((opt) => Number(opt.id) === scheduleId)?.label ||
        `Contribution Schedule #${scheduleId}`
      );
    }

    if (Number(row.scheduleTypeId) === 3) {
      return fineOptions.find((opt) => Number(opt.id) === scheduleId)?.label || `Fine Schedule #${scheduleId}`;
    }

    return `Schedule #${scheduleId}`;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Box mb={1}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Transaction #{transaction?.id ?? "-"} • Ref: {transaction?.transactionReference ?? "-"}
            </Typography>

            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="h6">
                Amount: KES {currency(amount)} • Breakdown Total: KES {currency(total)}
              </Typography>

              {!isViewMode && (
                <Chip
                  size="small"
                  label={mismatch ? "Total mismatch" : "Total OK"}
                  color={mismatch ? "warning" : "success"}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Box component={Paper} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Breakdowns</Typography>

              {!isViewMode && (
                <Tooltip title="Add another line item (Loan / Contribution / Fine)">
                  <span>
                    <Button startIcon={<Add />} onClick={addRow} disabled={loading}>
                      Add Breakdown
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {breakdowns.map((row, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                {isViewMode ? (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Breakdown #{idx + 1}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Schedule Type
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.scheduleType || SCHEDULE_TYPES.find((t) => t.id === Number(row.scheduleTypeId))?.label || "-"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Schedule
                      </Typography>
                      <Typography variant="body2">{getScheduleLabel(row)}</Typography>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Allocated Amount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        KES {currency(row.allocatedAmount)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Status: <strong>{row.status ?? "-"}</strong>
                        <StatusChip value={row.status} />
                      </Typography>

                      {row.processingErrorMessage ? (
                        <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                          Error: {row.processingErrorMessage}
                        </Typography>
                      ) : null}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        fullWidth
                        label="Schedule Type"
                        value={row.scheduleTypeId}
                        onChange={(e) => handleTypeChange(idx, e.target.value)}
                        disabled={loading || isViewMode}
                      >
                        {SCHEDULE_TYPES.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Selector based on type */}
                    {Number(row.scheduleTypeId) === 1 && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Loan Repayment Schedule"
                          value={row.scheduleId}
                          onChange={(e) => updateRow(idx, "scheduleId", e.target.value)}
                          disabled={loading || loadingLoans || isViewMode}
                        >
                          {loanOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}

                    {Number(row.scheduleTypeId) === 2 && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Contribution Schedule"
                          value={row.scheduleId}
                          onChange={(e) => updateRow(idx, "scheduleId", e.target.value)}
                          disabled={loading || loadingContrib || isViewMode}
                        >
                          {contribOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}

                    {Number(row.scheduleTypeId) === 3 && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Fine Schedule"
                          value={row.scheduleId}
                          onChange={(e) => updateRow(idx, "scheduleId", e.target.value)}
                          disabled={loading || loadingFines || isViewMode}
                        >
                          {fineOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}

                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Allocated Amount (KES)"
                        value={row.allocatedAmount}
                        onChange={(e) => updateRow(idx, "allocatedAmount", e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                        disabled={loading || isViewMode}
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Status: <strong>{row.status ?? "-"}</strong>
                            <StatusChip value={row.status} />
                          </Typography>

                          {row.processingErrorMessage ? (
                            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
                              Error: {row.processingErrorMessage}
                            </Typography>
                          ) : null}
                        </Box>

                        {!isViewMode && (
                          <IconButton onClick={() => removeRow(idx)} disabled={loading || breakdowns.length === 1}>
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            ))}
          </Box>

          <Box mt={1}>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              {isViewMode
                ? "This is a read-only view of the breakdowns recorded for this transaction."
                : "Note: The breakdown total should match the transaction amount before approval."}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {isViewMode ? "Close" : "Cancel"}
        </Button>

        {!isViewMode && (
          <Button variant="contained" onClick={submit} disabled={loading || !canSubmit}>
            {loading ? "Saving..." : "Save Breakdown"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransactionBreakdownForm;
